import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "components/blocks/Modal";
import { BackendServices } from "services";
import {
  HelpUsResult,
  HELP_US_TYPE_LINK,
  HELP_US_TYPE_PDF,
} from "services/helpUsService/models/helpUsTypes";

type HelpUsModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type TabKey = typeof HELP_US_TYPE_LINK | typeof HELP_US_TYPE_PDF;

const HelpUsModal = ({ isOpen, onClose }: HelpUsModalProps) => {
  const { t } = useTranslation("Shared");
  const [helpUsData, setHelpUsData] = useState<HelpUsResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>(HELP_US_TYPE_PDF);

  useEffect(() => {
    if (isOpen) {
      fetchHelpUsData();
    }
  }, [isOpen]);

  const fetchHelpUsData = async () => {
    setLoading(true);
    try {
      const response = await BackendServices.helpUsService.GetAllHelpUs({
        pageIndex: 0,
        pageSize: 0,
      });

      if (!response.hasError && response.data?.result) {
        setHelpUsData(response.data.result);
      }
    } catch (error) {
      console.error("Error fetching help data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeLabel = (item: HelpUsResult) =>
    item.helpUsType?.helpUsTypeDetail?.displayName;

  const tabs = useMemo(() => {
    const linkLabel =
      helpUsData.find((i) => i.helpUsTypeId === HELP_US_TYPE_LINK && getTypeLabel(i))
        ?.helpUsType?.helpUsTypeDetail?.displayName || t("Link");
    const pdfLabel =
      helpUsData.find((i) => i.helpUsTypeId === HELP_US_TYPE_PDF && getTypeLabel(i))
        ?.helpUsType?.helpUsTypeDetail?.displayName || "PDF";
    return [
      { key: HELP_US_TYPE_PDF as TabKey, label: pdfLabel },
      { key: HELP_US_TYPE_LINK as TabKey, label: linkLabel },
    ];
  }, [helpUsData, t]);

  const visibleItems = helpUsData.filter((i) => i.helpUsTypeId === activeTab);

  const handleRowClick = (item: HelpUsResult) => {
    const url = item.helpUsDetail?.link;
    if (!url) return;
    if (item.helpUsTypeId === HELP_US_TYPE_PDF) {
      const link = document.createElement("a");
      link.href = url;
      link.download = item.helpUsDetail.name || "help-document.pdf";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t("Help")} size="lg">
      <div className="help-us-modal">
        <ul className="nav nav-tabs mb-3">
          {tabs.map((tab) => (
            <li className="nav-item" key={tab.key}>
              <button
                type="button"
                className={`nav-link ${activeTab === tab.key ? "active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>

        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
            <div className="custom-loader"></div>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>{t("Name")}</th>
                  <th className="text-center" style={{ width: "80px" }}>
                    {t("Actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.length > 0 ? (
                  visibleItems.map((item) => {
                    const isPdf = item.helpUsTypeId === HELP_US_TYPE_PDF;
                    const iconClass = isPdf
                      ? "bi bi-file-earmark-pdf-fill text-danger"
                      : "bi bi-link-45deg text-primary";
                    return (
                      <tr
                        key={item.id}
                        className="cursor-pointer"
                        onClick={() => handleRowClick(item)}
                      >
                        <td>{item.helpUsDetail?.name || "-"}</td>
                        <td className="text-center">
                          <i className={`${iconClass} fs-4`} title={t(isPdf ? "Download" : "Open")}></i>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={2} className="text-center text-muted">
                      {t("NoDataAvailable")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default HelpUsModal;
